import React, { useEffect, useState } from 'react';
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import NavigationDefault from 'app/components/Navigation/NavigationDefault';
import { NavigationDetail } from 'app/components/Navigation/core/types';
import RootContentPage from 'app/components/layout/RootContentPage';

import { useAuth } from 'app/modules/auth';

import { replacePath } from 'app/helpers';
import { useBoolean } from 'app/hooks';
import { usePolicyInUser } from 'app/hooks/policy';

import { PATH } from 'app/constants/path';

import UserTabletManagement from './UserTabletManagement';
import UserWebManagementPage from './UserWebManagement';
import RoutePolicy from 'app/routing/RoutePolicy';
import { useOrganizationStore } from 'theme/layout/components/aside/store';

const UsersManagementPage = () => {
  const { currentUser } = useAuth();
  const policy = usePolicyInUser(currentUser?.policy ?? []);
  const [navigation, setNavigation] = useState<NavigationDetail[]>([]);
  const isFirstNavigation = useBoolean(false);
  const location = useLocation();
  const navigate = useNavigate();
  const orgId = useOrganizationStore((state) => state.state.organization?.id);
  const params = useParams<{ orgId: string }>();
  const organizationId = params.orgId ? Number(params.orgId) : orgId;

  const isPageSystemUsers = location.pathname.includes(`${PATH.USER_MANAGEMENT_SYSTEM}`);

  useEffect(() => {
    if (!policy || isFirstNavigation.value) {
      return;
    }
    const newNavigation: NavigationDetail[] = [];
    if (policy.userManagement) {
      newNavigation.push({
        value: 1,
        title: 'Desktop account',
        path: isPageSystemUsers ? PATH.USER_MANAGEMENT_SYSTEM_WEB : PATH.WEB,
        systemPath: PATH.USER_MANAGEMENT_SYSTEM_WEB,
        normalPath: PATH.WEB,
      });
    }

    if (policy.userMMD || policy.userNurse || policy.userManagement) {
      newNavigation.push({
        value: 2,
        title: 'Tablet account',
        path: isPageSystemUsers ? PATH.USER_MANAGEMENT_SYSTEM_TABLET : PATH.TABLET,
        systemPath: PATH.USER_MANAGEMENT_SYSTEM_TABLET,
        normalPath: PATH.TABLET,
      });
    }
    setNavigation(newNavigation);
    isFirstNavigation.setValue(true);
  }, [policy, isPageSystemUsers]);

  const getActiveTabValue = () => {
    const path = location.pathname;
    if (path.includes(PATH.WEB) || path.includes(PATH.USER_MANAGEMENT_SYSTEM_WEB)) return 1;
    if (path.includes(PATH.TABLET) || path.includes(PATH.USER_MANAGEMENT_SYSTEM_TABLET)) return 2;
    return 0;
  };

  const handleNavigationChange = (item: NavigationDetail) => () => {
    const targetPath = isPageSystemUsers ? item.systemPath : item.normalPath;
    navigate(`/org/${organizationId}${targetPath}`);
  };

  return (
    <Routes>
      <Route
        element={
          <React.Fragment>
            <RootContentPage title="User accounts">
              <NavigationDefault
                navigation={navigation}
                valueActive={getActiveTabValue()}
                onCustomNavigate={handleNavigationChange}
              />
              <Outlet />
            </RootContentPage>
          </React.Fragment>
        }
      >
        <Route
          path={replacePath(PATH.WEB, PATH.USERS, '')}
          element={
            <RoutePolicy accept={policy.userManagement}>
              <UserWebManagementPage />
            </RoutePolicy>
          }
        />

        <Route
          path={replacePath(PATH.TABLET, PATH.USERS, '')}
          element={
            <RoutePolicy accept={policy.userManagement || policy.userMMD || policy.userNurse}>
              <UserTabletManagement />
            </RoutePolicy>
          }
        />
        <Route index element={<Navigate to={policy.userManagement ? PATH.WEB : PATH.TABLET} />} />
      </Route>
    </Routes>
  );
};

export default UsersManagementPage;
